import "@/styles/globals.css";
import "@/styles/roboto.css";

import type { PropsWithChildren } from "react";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
} from "react-router";

import { IqfAxiosError } from "iqf-web-ui/api-fetch";
import { SentryErrorBoundary } from "iqf-web-ui/sentry";
import { environmentDocumentTitle } from "udzs-common/components/environment-banner";
import { RouteLoadingBar } from "udzs-common/components/route-loading-bar";

import type { Route } from "./+types/root";
import { pipAuthClient } from "./auth/client";
import { requirePipMe } from "./auth/route-auth";
import { PageError } from "./components/page/page-error";
import { ENV } from "./env";
import { LocaleProvider } from "./providers/locale-provider";
import {
  QueryClientProvider,
  queryClient,
} from "./providers/query-client-provider";
import { SentryProvider } from "./providers/sentry-provider";
import { SettingsProvider } from "./providers/settings-provider";
import { WebSocketProvider } from "./providers/websocket-provider";
import { normalizePath } from "./utils/url";

export const links: Route.LinksFunction = () => {
  const base = normalizePath(ENV.VITE_BASENAME, true);

  return [
    {
      rel: "icon",
      type: "image/x-icon",
      href: `${base}favicon.ico`,
    },
  ];
};

export const clientMiddleware: Route.ClientMiddlewareFunction[] = [
  async ({ request }, next) => {
    const url = new URL(request.url);

    if (pipAuthClient.isOAuthFlowPath(url.pathname)) {
      return next();
    }

    await requirePipMe(request, queryClient);

    return next();
  },
];

export function Layout({ children }: PropsWithChildren) {
  return (
    <html lang="sk">
      <head>
        <title>
          {environmentDocumentTitle(ENV.VITE_ENVIRONMENT_LABEL, "UDZS - PIP")}
        </title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        <LocaleProvider>
          <SentryProvider>
            <QueryClientProvider>
              <WebSocketProvider>
                <SettingsProvider>{children}</SettingsProvider>
              </WebSocketProvider>
            </QueryClientProvider>
          </SentryProvider>
        </LocaleProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  if (isRouteErrorResponse(error) || error instanceof IqfAxiosError) {
    if (error.status === 401 || error.status === 403 || error.status === 404) {
      return <PageError status={error.status} />;
    }
  }

  return (
    <SentryErrorBoundary feature="router" contexts={{ source: "root" }}>
      <ThrowRouteError error={error} />
    </SentryErrorBoundary>
  );
}

function ThrowRouteError({ error }: { error: unknown }) {
  if (error) {
    throw error;
  }

  return null;
}

export default function App(_props: Route.ComponentProps) {
  return (
    <>
      <RouteLoadingBar />
      <Outlet />
    </>
  );
}
